// AppLayout — 2-tier sidebar (AdminSidebar) + main outlet.
// Visual = Core's Layout: bg-slate-50, lg:pl-[320px] khi expanded / lg:pl-[80px] khi compact.
//
// Props:
//   - menu: Core's adminMenu schema [{ key, label, icon, basePath, items?: [{ key, path, label, group?, end? }] }]
//   - brand: { logo?, appName, subtitle?, appShort? }
//   - homeLink?: { to, label?, shortLabel? }
//   - detailPatterns?: [RegExp] — routes ẩn secondary (ẩn cả 240px panel, chỉ giữ 80px rail)
//   - fullscreenPatterns?: [RegExp] — routes content fullscreen (vd /inbox)
//   - customSecondary?: { [groupKey]: ReactComponent } — override secondary với custom panel
//   - badgeCounts?: { [itemKey]: number }

import { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AdminSidebar from './sidebar/AdminSidebar.jsx';
import WebPushRegister from '../chat/WebPushRegister.jsx';
import ChatDock from '../chat-dock/index.js';
import { findActiveGroup } from '../../navigation/menuUtils.js';
import { setHostMenu } from '../../navigation/menuRegistry.js';
import { NavProgressBar, RouteFallback } from '../common/RouteProgress.jsx';

// Content-area fallback — khung xám giữ bố cục, chỉ trong vùng <main>, KHÔNG cover sidebar.
// Fix giật (2026-05-28): trước đây Suspense bọc cả <Routes> ở App.jsx → click menu
// → lazy page suspend → fallback thay TOÀN BỘ gồm sidebar → "load lại hết + giật".
// Suspense nội bộ này bắt lazy child TRƯỚC khi tới Suspense ngoài → sidebar đứng yên.
// 25.08.2026: spinner giữa khoảng trống đổi sang <RouteFallback/> — vùng nội dung rỗng
// vẫn bị đọc là "trắng trang"; khung xám giữ được hình hài trang.
const ContentFallback = RouteFallback;

export default function AppLayout({
  menu = [],
  brand,
  homeLink,
  detailPatterns = [],
  fullscreenPatterns = [],
  fullbleedPatterns = [],          // NEW: routes content sát top + full width (no padding) nhưng vẫn show 2-tier sidebar
  customSecondary,
  badgeCounts,
  // Slot của SidebarPrimary — forward xuống AdminSidebar (nó đã nhận sẵn 2 prop này).
  // primaryBottomSlot thay avatar mặc định (SaaS pass <UserMenu/> để có popup cá nhân
  // + đổi ngôn ngữ 1 cú bấm); không pass thì giữ avatar-link → /me như cũ.
  primaryTopSlot,
  primaryBottomSlot,
  showChatDock = false,            // chat nổi Messenger-style (treo góc phải, ẩn ở /chat)
  children,
}) {
  const location = useLocation();
  // Đăng ký menu host → page /permissions đọc để dựng matrix đúng theo menu thật
  // (group=menu chính, sub-menu=quyền). AppLayout render trước page con.
  setHostMenu(menu);
  const [autoHide, setAutoHide] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('sidebar_auto_hide') === 'true');

  useEffect(() => {
    const onStorage = () => setAutoHide(localStorage.getItem('sidebar_auto_hide') === 'true');
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const activeGroup = useMemo(() => findActiveGroup(menu, location.pathname), [menu, location.pathname]);
  const isSingleLinkGroup = !!(activeGroup && !activeGroup.items?.length);
  const isDetailPage = detailPatterns.some((p) => p.test(location.pathname));
  const isFullscreen = fullscreenPatterns.some((p) => p.test(location.pathname));
  const isFullbleed = fullbleedPatterns.some((p) => p.test(location.pathname));
  // isFullscreen cũng compact (match Core Layout.jsx:100) — overlay fullscreen
  // phải start từ x=80 (primary rail), không chừa 240px cho secondary sidebar.
  const isCompact = autoHide || isDetailPage || isSingleLinkGroup || isFullscreen;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Thanh tiến trình 2px trên đỉnh — với v7_startTransition, React giữ trang cũ trong
          lúc tải chunk trang mới; thiếu thanh này thì bấm xong "im như không nhận". */}
      <NavProgressBar />
      <AdminSidebar
        menu={menu}
        brand={brand}
        homeLink={homeLink}
        detailPatterns={detailPatterns}
        fullscreenPatterns={fullscreenPatterns}
        customSecondary={customSecondary}
        badgeCounts={badgeCounts}
        primaryTopSlot={primaryTopSlot}
        primaryBottomSlot={primaryBottomSlot}
      />
      {/* Web push register — chạy 1 lần khi user login + ở mọi page admin */}
      <WebPushRegister />
      {/* Chat nổi Messenger-style — treo góc phải mọi trang (tự ẩn ở /chat) */}
      {showChatDock && <ChatDock />}
      <main className={isCompact ? 'lg:pl-[80px]' : 'lg:pl-[320px]'}>
        {isFullscreen ? (
          <div className={`fixed inset-0 overflow-hidden ${isCompact ? 'lg:left-[80px]' : 'lg:left-[320px]'}`}>
            <Suspense fallback={<ContentFallback />}>{children ?? <Outlet />}</Suspense>
          </div>
        ) : isFullbleed ? (
          // Fullbleed: content sát top + full width, KHÔNG padding (page tự lo header sticky).
          // Khớp Core's isApprovalsFullBleed: /approvals + /me + /automation + /support + ...
          <div className="h-screen overflow-hidden">
            <Suspense fallback={<ContentFallback />}>{children ?? <Outlet />}</Suspense>
          </div>
        ) : (
          // pb-24: chừa đáy cho nội dung thở + không bị nút chat nổi (góc phải dưới,
          // ~56px + margin) đè lên hàng cuối. pb-8 cũ khiến card cuối dính sát mép.
          <div className="px-4 pt-20 pb-24 lg:px-8 lg:pt-8">
            <Suspense fallback={<ContentFallback />}>{children ?? <Outlet />}</Suspense>
          </div>
        )}
      </main>
    </div>
  );
}
